import { useState } from 'react';

import { useDynamicOverflowContext } from '#@/plumbing/DynamicOverflow';

import HoverableText from '../HoverableText';
import { BreadcrumbCurrent, BreadcrumbLink, ChevronIcon } from './Styles';
import type { BreadcrumbData } from './types';

const MIN_LABEL_WIDTH_IN_PX = 64;

interface BreadcrumbProps {
  index: number;
  label: string;
  totalItems: number;
  to?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ index, label, totalItems, to }) => {
  const { registerElement, isElementHidden } = useDynamicOverflowContext<BreadcrumbData>();
  const [labelElement, setLabelElement] = useState<HTMLElement | null>(null);

  const elementKey = `breadcrumb${index}`;

  if (isElementHidden(elementKey)) {
    return null;
  }

  const registerFunc = (element: unknown) =>
    registerElement(elementKey, element as HTMLElement, { label, to });

  // We want wide breadcrumbs to be truncated up to a certain minimum width, but we also
  // want short breadcrumbs to be shorter than this minimum width.
  const labelMinWidth =
    (labelElement?.offsetWidth ?? 0) >= MIN_LABEL_WIDTH_IN_PX
      ? MIN_LABEL_WIDTH_IN_PX
      : labelElement?.offsetWidth;

  if (!to) {
    return (
      <BreadcrumbCurrent ref={registerFunc} $minWidth={labelMinWidth}>
        <HoverableText text={<span ref={setLabelElement}>{label}</span>} />
      </BreadcrumbCurrent>
    );
  }

  // We want to truncate the first breadcrumb (if needed) if all breadcrumbs between the first
  // and last one are already hidden. Since breadcrumbs are hidden from left to right, if the
  // second-to-last breadcrumb is hidden, it means there are no more breadcrumbs left to hide.
  //
  // For the case of only 2 breadcrumbs, there are never any breadcrumbs left to hide.
  // Fewer than 2 breadcrumbs means the component is used incorrectly.
  const noBreadcrumbsLeftToHide =
    totalItems === 2 || isElementHidden(`breadcrumb${totalItems - 2}`);
  const shouldLinkBeTruncated = index === 0 && noBreadcrumbsLeftToHide;

  return (
    <>
      <BreadcrumbLink
        to={to}
        ref={registerFunc}
        data-cy-breadcrumb
        $truncate={shouldLinkBeTruncated}
        $minWidth={labelMinWidth}
      >
        {shouldLinkBeTruncated ? (
          <HoverableText text={<span ref={setLabelElement}>{label}</span>} />
        ) : (
          label
        )}
      </BreadcrumbLink>
      <ChevronIcon />
    </>
  );
};

export default Breadcrumb;
